import React from 'react';
import FilterPill from '../FilterPill/FilterPill';
import { CardFinish, FINISH_LABELS, FINISH_ORDER } from 'utils/finishes';
import { FinishIcon } from 'components/FinishTag';
import './InlineFoilPills.css';

interface InlineFoilPillsProps {
  /** The accepted finishes (a WANT's intent is a set), any order */
  value: readonly CardFinish[];
  onChange: (next: CardFinish[]) => void;
  /**
   * Finishes the card exists in. A finish outside this list renders disabled and
   * unchecked (never hidden), even if the stored flag is true: an inert
   * acceptEtched on a card with no etched finish must not read as a choice
   * (Edge-Case Ruling, 2026-09-08). Omit to offer all three.
   */
  available?: readonly CardFinish[];
  disabled?: boolean;
}

/**
 * Multi-select finish pills for a WANT (Design Pass D-3): non-foil, foil, etched
 * in canonical order. The last-pill guard generalises from "don't let both go
 * off" to "don't let all three go off": a want must accept at least one finish.
 */
const InlineFoilPills: React.FC<InlineFoilPillsProps> = ({
  value,
  onChange,
  available,
  disabled = false,
}) => {
  const isAvailable = (f: CardFinish) => !available || available.includes(f);
  const active = (f: CardFinish) => value.includes(f) && isAvailable(f);

  const toggle = (finish: CardFinish) => {
    if (disabled || !isAvailable(finish)) return;
    const next = new Set(value.filter(isAvailable));
    if (next.has(finish)) {
      // Never let the set go empty
      if (next.size === 1) return;
      next.delete(finish);
    } else {
      next.add(finish);
    }
    onChange(FINISH_ORDER.filter((f) => next.has(f)));
  };

  return (
    <div className={`inline-foil-pills ${disabled ? 'disabled' : ''}`} role="group" aria-label="Accepted finishes">
      {FINISH_ORDER.map((finish) => (
        <FilterPill
          key={finish}
          label={FINISH_LABELS[finish]}
          icon={<FinishIcon finish={finish} size="tag" className="inline-foil-pill-icon" />}
          active={active(finish)}
          disabled={disabled || !isAvailable(finish)}
          title={isAvailable(finish) ? undefined : `${FINISH_LABELS[finish]}: this card does not exist in this finish`}
          onToggle={() => toggle(finish)}
        />
      ))}
    </div>
  );
};

export default InlineFoilPills;
